import IconBin from "@shared/components/icons/Bin"
import IconCog1 from "@shared/components/icons/Cog1"
import IconCogLine from "@shared/components/icons/CogLine"
import IconCommonFileDouble from "@shared/components/icons/CommonFileDouble"
import IconCopy from "@shared/components/icons/Copy"
import IconDoubleArrowRight from "@shared/components/icons/DoubleArrowRight"
import IconNavArrowRight from "@shared/components/icons/NavArrowRight"
import IconSubtractCircle from "@shared/components/icons/SubtractCircle"
import IconView from "@shared/components/icons/View"
import IconViewOff from "@shared/components/icons/ViewOff"
import {
  DrilldownMenu,
  DrilldownRow,
  HeaderWithBackArrow,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  Row,
  RowContent,
  RowIcon,
  RowLabel,
  Rows,
  Section,
  SectionChevron,
  SectionHeading,
  SectionIcon,
  Sections,
  SectionTitle,
} from "@shared/components/menus/DrilldownMenu"
import { ConfirmView } from "@shared/components/menus/views/ConfirmView"
import { InfoView } from "@shared/components/menus/views/InfoView"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import { tracked } from "@supergrain/kernel/react"
import CourseHeaderMenuTrigger from "./CourseHeaderMenuTrigger"
import { expandedSections } from "./lesson-menu-sections"

/** Why the class isn't meeting — each cause is turned back on a different way, or not at all. */
export type OffCause = "note" | "schedule" | "planbook"

interface Props {
  course: Course
  courseDate: CourseDateCombined
  offCause: OffCause
  /** Whether the viewer may change the day at all; a read-only viewer only gets Copy Lesson. */
  canEdit: boolean
  /** Cancels whatever is holding the day off. The day's notes are left exactly as they are. */
  turnClassBackOn: () => void
  /** Keeps the day off but stops painting its notes, which drops the class from the day. */
  hideClassToday: () => void
  /** Opens the edit-course modal for this course. */
  editCourseTitleAndOrder: (courseId: string) => void
  /* The three below appear only for a lesson stranded on the off day. */
  bumpLessonForward: () => void
  copyLesson: () => void
  eraseLesson: () => void
}

const TURN_ON_LABEL: Record<OffCause, string> = {
  note: "Turn class back on",
  schedule: "Have class today anyway",
  planbook: "Turn class back on",
}

/**
 * The Lesson Menu on a day the class isn't meeting — the same menu, with the rows that need a
 * lesson taken out. A slot with no lesson has nothing to paste, template, print or share; what
 * is left is whether the class meets today, plus, when a lesson is stranded here, the rows that
 * clear it, which is what the dangling-lesson notice tells the user to do.
 */
export default tracked(function ClassOffMenu({
  course,
  courseDate,
  offCause,
  canEdit,
  turnClassBackOn,
  hideClassToday,
  editCourseTitleAndOrder,
  bumpLessonForward,
  copyLesson,
  eraseLesson,
}: Props) {
  const expandedIds = expandedSections.expandedIds

  const lessonHeader = (title: string) =>
    function Header({ back }: { back: () => void }) {
      return (
        <HeaderWithBackArrow backTo="Lesson Menu" onBack={back}>
          {title}
        </HeaderWithBackArrow>
      )
    }

  return (
    <DrilldownMenu
      ariaLabel="Lesson menu"
      trigger={<CourseHeaderMenuTrigger label="Lesson Menu" dataTest="lesson menu trigger" />}
      header={
        <MenuHeader>
          <MenuTitle>Lesson Menu</MenuTitle>
        </MenuHeader>
      }
    >
      {({ close }) => {
        const closeThen = (action: () => void) => () => {
          close()
          action()
        }

        return (
          <MenuRoot>
            <Sections>
              {canEdit && (
                <Section
                  typeaheadText="Show/Hide"
                  isExpanded={expandedIds.has("show-hide")}
                  onExpandedChange={(isExpanded) =>
                    expandedSections.setExpanded("show-hide", isExpanded)
                  }
                  heading={
                    <SectionHeading>
                      <SectionIcon icon={IconViewOff} />
                      <SectionTitle>Show/Hide</SectionTitle>
                      <SectionChevron />
                    </SectionHeading>
                  }
                >
                  <Rows>
                    {/* The inverse of the Lesson Menu's "Remove this class today". */}
                    {offCause === "planbook" ? (
                      // A planbook-wide day off can't be cancelled from one class, so this says
                      // where to go instead of offering a press that would quietly do nothing.
                      <DrilldownRow
                        typeaheadText="Turn class back on"
                        header={lessonHeader("The whole day is off")}
                        body={({ back }) => (
                          <InfoView
                            body="Today is off for the entire planbook, so this class can’t meet on its own. Use the Day Menu to turn the day back on."
                            onDone={back}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconView} />
                          <RowLabel>{TURN_ON_LABEL.planbook}</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    ) : offCause === "schedule" ? (
                      <DrilldownRow
                        typeaheadText={TURN_ON_LABEL.schedule}
                        header={lessonHeader("Have this class today?")}
                        body={({ back }) => (
                          <ConfirmView
                            body="This class is off every one of these days. Turning it on affects today only."
                            confirmLabel={TURN_ON_LABEL.schedule}
                            onCancel={back}
                            onConfirm={closeThen(turnClassBackOn)}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconView} />
                          <RowLabel>{TURN_ON_LABEL.schedule}</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    ) : (
                      // Nothing to confirm: the day's notes stay either way, so this only gives
                      // the user back a class they can turn off again.
                      <Row typeaheadText={TURN_ON_LABEL.note} onAction={closeThen(turnClassBackOn)}>
                        <RowContent>
                          <RowIcon icon={IconView} />
                          <RowLabel>{TURN_ON_LABEL.note}</RowLabel>
                        </RowContent>
                      </Row>
                    )}

                    {courseDate.hasVisibleAnnotations && (
                      <DrilldownRow
                        typeaheadText="Hide this class today"
                        header={lessonHeader("Hide this class today?")}
                        body={({ back }) => (
                          <ConfirmView
                            body={
                              courseDate.visibleAnnotations.length > 1
                                ? "We'll have to delete your notes, too."
                                : "We'll have to delete your note, too."
                            }
                            confirmLabel="Hide Class"
                            destructive
                            onCancel={back}
                            onConfirm={closeThen(hideClassToday)}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconViewOff} />
                          <RowLabel>Hide this class today</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    )}
                  </Rows>
                </Section>
              )}

              {courseDate.hasLesson && (
                <Section
                  typeaheadText="Copy and Paste"
                  isExpanded={expandedIds.has("copy-paste")}
                  onExpandedChange={(isExpanded) =>
                    expandedSections.setExpanded("copy-paste", isExpanded)
                  }
                  heading={
                    <SectionHeading>
                      <SectionIcon icon={IconCommonFileDouble} />
                      <SectionTitle>Copy and Paste</SectionTitle>
                      <SectionChevron />
                    </SectionHeading>
                  }
                >
                  <Rows>
                    <Row typeaheadText="Copy Lesson" onAction={closeThen(copyLesson)}>
                      <RowContent>
                        <RowIcon icon={IconCopy} />
                        <RowLabel>Copy Lesson</RowLabel>
                      </RowContent>
                    </Row>
                  </Rows>
                </Section>
              )}

              {canEdit && courseDate.hasLesson && (
                <Section
                  typeaheadText="Move"
                  isExpanded={expandedIds.has("move")}
                  onExpandedChange={(isExpanded) =>
                    expandedSections.setExpanded("move", isExpanded)
                  }
                  heading={
                    <SectionHeading>
                      <SectionIcon icon={IconDoubleArrowRight} />
                      <SectionTitle>Move</SectionTitle>
                      <SectionChevron />
                    </SectionHeading>
                  }
                >
                  <Rows>
                    <Row
                      typeaheadText="Bump Lesson Forward"
                      onAction={closeThen(bumpLessonForward)}
                    >
                      <RowContent>
                        <RowIcon icon={IconNavArrowRight} />
                        <RowLabel>Bump Lesson Forward</RowLabel>
                      </RowContent>
                    </Row>
                  </Rows>
                </Section>
              )}

              {canEdit && courseDate.hasLesson && (
                <Section
                  typeaheadText="Remove"
                  isExpanded={expandedIds.has("remove")}
                  onExpandedChange={(isExpanded) =>
                    expandedSections.setExpanded("remove", isExpanded)
                  }
                  heading={
                    <SectionHeading>
                      <SectionIcon icon={IconSubtractCircle} />
                      <SectionTitle>Remove</SectionTitle>
                      <SectionChevron />
                    </SectionHeading>
                  }
                >
                  <Rows>
                    <DrilldownRow
                      typeaheadText="Erase Lesson"
                      header={lessonHeader("Delete the lesson?")}
                      body={({ back }) => (
                        <ConfirmView
                          confirmLabel="Delete"
                          destructive
                          onCancel={back}
                          onConfirm={closeThen(eraseLesson)}
                        />
                      )}
                    >
                      <RowContent>
                        <RowIcon icon={IconBin} />
                        <RowLabel>Erase Lesson</RowLabel>
                      </RowContent>
                    </DrilldownRow>
                  </Rows>
                </Section>
              )}

              {canEdit && (
                <Section
                  typeaheadText="Settings"
                  isExpanded={expandedIds.has("settings")}
                  onExpandedChange={(isExpanded) =>
                    expandedSections.setExpanded("settings", isExpanded)
                  }
                  heading={
                    <SectionHeading>
                      <SectionIcon icon={IconCog1} />
                      <SectionTitle>Settings</SectionTitle>
                      <SectionChevron />
                    </SectionHeading>
                  }
                >
                  <Rows>
                    <Row
                      typeaheadText="Edit Class Settings"
                      onAction={closeThen(() => editCourseTitleAndOrder(course.id))}
                    >
                      <RowContent>
                        <RowIcon icon={IconCogLine} />
                        <RowLabel>Edit Class Settings</RowLabel>
                      </RowContent>
                    </Row>
                  </Rows>
                </Section>
              )}
            </Sections>
          </MenuRoot>
        )
      }}
    </DrilldownMenu>
  )
})
